// Single source of truth for every word and link on the page.
// Edit here, not in the components.
//
// Lines marked TODO(rivo) are inferred from repo names / old markup and were
// never confirmed. Replace them with the real thing.

// Images are WebP, generated from the originals by `npm run optimize:img`.

import portrait from "../img/Rivo2.webp";

import simakip from "../img/simakip.webp";
import teknoka from "../img/Teknoka10.webp";
import karsanusa from "../img/karsanusa.webp";
import presentPdf from "../img/presentpdf.webp";
import impostor from "../img/whoistheimpostor.webp";
import animalClassifier from "../img/animal-classifier.webp";
import helpMedic from "../img/help-medic.webp";
import gesture from "../img/gesture.webp";
import makanan from "../img/Makanan.webp";

import googleData from "../img/googledata.webp";
import googleR from "../img/WithR.webp";
import ibmData from "../img/IBMData.webp";
import deepLearning from "../img/DeepL.webp";
import itSupport from "../img/ITSupport.webp";
import machineLearning from "../img/ML.webp";
import docker from "../img/Docker.webp";
import django from "../img/Django.webp";
import second from "../img/Second.webp";

export const person = {
  name: "Rivo Juniandra Rumadi",
  short: "Rivo",
  role: "Software Engineer",
  location: "Indonesia",
  // Shown under the name in the hero. Two sentences, no more.
  statement:
    "I build web applications, and I train the models that go inside them. Mostly React and Laravel on the front of the problem, Python on the back of it.",
  portrait,
  portraitAlt: "Rivo Juniandra Rumadi, seated, arms folded",
  // The paragraph that carries the whole page. His own words, tightened.
  about:
    "Self driven, quick starter, curious. I like problems that are messy in the real world before they are code, and I would rather ship something that holds up than something that demos well.",
  cv: process.env.PUBLIC_URL + "/CvRivo.pdf",
};

export const links = {
  github: "https://github.com/Leon24k",
  linkedin: "https://www.linkedin.com/in/rumadirivo/",
  instagram: "https://www.instagram.com/rumadirivo/",
  whatsapp: "https://wa.me/6285156649646?text=Hy!",
};

export const nav = [
  { id: "work", label: "Work" },
  { id: "approach", label: "Approach" },
  { id: "credentials", label: "Credentials" },
  { id: "contact", label: "Contact" },
];

// Ordered by what a stranger should see first: shipped work for other people,
// then the open source, then the coursework. `repo` is optional and renders as
// a second link next to the year.
export const projects = [
  {
    title: "SIMAKIP UHAMKA",
    // TODO(rivo): year, and your actual role on it (built it? maintained it?
    // part of a team?). Right now the row only claims you worked on it.
    year: "",
    summary:
      "Research and community service management system for UHAMKA. Lecturers submit proposals, reviewers score them against a fixed instrument, and funded output lands in a public publication catalogue.",
    stack: ["Laravel", "MySQL"],
    href: "https://simakip.uhamka.ac.id/",
    image: simakip,
    alt: "SIMAKIP UHAMKA home page",
  },
  {
    title: "TEKNOKA 10",
    year: "2025",
    summary:
      "Official site for UHAMKA's 10th national technology seminar, on building sustainable industries through digital transformation. Carried the schedule, speakers and registration for several hundred researchers and attendees.",
    stack: ["CodeIgniter"],
    href: "",
    image: teknoka,
    alt: "TEKNOKA 10 seminar website",
  },
  {
    title: "KarsaNusa",
    year: "2024",
    summary:
      "Bangkit capstone with a team of seven: an Android app for exploring Indonesian cultural heritage. I worked the machine learning path, alongside the mobile and cloud tracks.",
    stack: ["TensorFlow", "Kotlin", "Google Cloud"],
    href: "https://github.com/KarsaNusa",
    image: karsanusa,
    alt: "KarsaNusa project logo",
  },
  {
    title: "PresentPDF",
    // TODO(rivo): year is the repo's last push, change it if the project is older
    year: "2026",
    summary:
      "Presents PDF slides exported from Canva, Gamma or Figma with 3D transitions, a laser pointer and live pen annotation. Runs entirely in the browser, and files purge themselves after 24 hours.",
    stack: ["Svelte 5", "Vite", "pdf.js"],
    href: "https://github.com/Leon24k/PresentPDF",
    image: presentPdf,
    alt: "PresentPDF upload screen",
  },
  {
    title: "Who Is The Impostor",
    year: "2026",
    summary:
      "Party game for one shared phone. Everyone sees a secret word except the impostor, who sees only the category, then the table argues and votes.",
    stack: ["React", "Tailwind"],
    href: "https://whoistheimpostorgame.vercel.app/",
    image: impostor,
    alt: "Who Is The Impostor secret word card",
  },
  {
    title: "Animal Classifier",
    year: "2026",
    summary:
      "Upload a photo, get the species. A trained model identifies 90 animals in the browser and returns what it knows about the one it found.",
    stack: ["TypeScript", "TensorFlow"],
    href: "https://animal-classifier-five.vercel.app/",
    repo: "https://github.com/Leon24k/animal-classifier-app",
    image: animalClassifier,
    alt: "Animal Classifier home page",
  },
  {
    title: "HelpMedic",
    // TODO(rivo): add the repo or live URL, and a sharper line on what it does.
    // Until there is a link the row is the only place a visitor can learn about it.
    year: "",
    summary: "Medical assistance interface.",
    stack: [],
    href: "",
    image: helpMedic,
    alt: "HelpMedic interface",
  },
  {
    title: "Hand Gesture Recognition",
    year: "",
    summary:
      "Controls system volume from a webcam feed by measuring the distance between thumb and index finger.",
    stack: ["Python", "OpenCV"],
    href: "https://github.com/Leon24k/Hand-Gesture",
    image: gesture,
    alt: "Hand gesture recognition output",
  },
  {
    title: "Pemesanan Makanan",
    // TODO(rivo): confirm year. This is the weakest row left, cut it once you
    // have another piece of real work to put here.
    year: "",
    summary:
      "Food ordering web app. Menu, cart and order flow for a small restaurant.",
    stack: ["PHP", "MySQL"],
    href: "https://github.com/Leon24k/pemesananMakanan",
    image: makanan,
    alt: "Food ordering web app interface",
  },
];

export const approach = {
  // TODO(rivo): these are the four from the old "My Tools & Stuff" section.
  // Cut any you no longer use, the list is stronger short.
  tools: [
    { name: "VS Code", note: "Daily driver for anything that ships to a browser." },
    { name: "PyCharm", note: "Second driver, for Python and data work." },
    { name: "Lightroom", note: "Photography. It is where the eye gets trained." },
    { name: "Git", note: "Everything, always, from the first commit." },
  ],
  principles: [
    {
      title: "Learn in public",
      body: "New things get built, not read about. The repo is the notebook.",
    },
    {
      title: "Web first, model second",
      body: "A model nobody can reach is a research paper. I build the surface it lives behind.",
    },
    {
      title: "Photography habit",
      body: "Framing, contrast, restraint. The same three decisions as an interface.",
    },
  ],
};

export const certificates = [
  {
    title: "Google Data Analytics",
    issuer: "Google",
    href: "https://drive.google.com/file/d/1XMh_FOV45DiqThdEte78IASJVs0cu4Ld/view?usp=sharing",
    image: googleData,
  },
  {
    title: "Machine Learning",
    issuer: "Coursera", // TODO(rivo): confirm issuer
    href: "https://drive.google.com/file/d/1fMOcnac7ULIpyTVa61XkUVYFrFLVeTAl/view?usp=sharing",
    image: machineLearning,
  },
  {
    title: "IT Support",
    issuer: "Google",
    href: "https://drive.google.com/file/d/1R0FZLHn_P5LpiLmkdg-_fzXDpM17CmNj/view?usp=sharing",
    image: itSupport,
  },
  {
    title: "Data Analysis with R",
    issuer: "Google",
    href: "https://drive.google.com/file/d/1ge10hMpCIBYCMfNJcIqaWkgKh4_L3KTu/view?usp=sharing",
    image: googleR,
  },
  {
    title: "Data Science",
    issuer: "IBM",
    href: "https://drive.google.com/file/d/1ZYLgmZwqaaCjfvHrxz9Xd5e4b15_WN5I/view?usp=sharing",
    image: ibmData,
  },
  {
    title: "Deep Learning",
    issuer: "DeepLearning.AI",
    href: "https://drive.google.com/file/d/1lPKNj1F-flFrDKgoeYPu88wVwgh6kfol/view",
    image: deepLearning,
  },
  {
    title: "Docker",
    issuer: "", // TODO(rivo): issuer
    href: "https://drive.google.com/file/d/1MoTAFIPNGXqYl1JRbKQjcsePZJx4yafM/view?usp=sharing",
    image: docker,
  },
  {
    title: "Django",
    issuer: "", // TODO(rivo): issuer
    href: "https://drive.google.com/file/d/1tb4qkvLIL2-Rjx16j1LF7MacRLREqYdO/view?usp=sharing",
    image: django,
  },
  {
    title: "Certificate", // TODO(rivo): this one was labelled "Second". Give it its real name.
    issuer: "",
    href: "https://drive.google.com/file/d/1OV5g-_raT4il7sGzET96L4bOJP59i80n/view?usp=sharing",
    image: second,
  },
];
